import { useQuery } from "@tanstack/react-query";
import { Link, Navigate } from "react-router-dom";
import { format } from "../lib/format";

import { rollupForDay } from "../lib/api";
import { currentClaims } from "../lib/auth";
import { adminRootPath, canSeeAdmin } from "../lib/adminNav";
import { roleHome } from "../lib/roleHome";

/** Landing page. Fetches `/api/rollups/day/<today>` which returns
 * one row per scope visible to the caller — the backend does all
 * the role-aware fan-out; the UI just renders whatever comes back.
 * That keeps the page identical across every role and puts the
 * source-of-truth in one place. */
export function Home() {
  const claims = currentClaims();

  // Roles with their own dedicated console land there instead of the
  // generic rollup grid — both on fresh login (Login sends everyone to
  // "/") and on any later visit to the home page.
  const home = roleHome(claims?.role);
  if (home) return <Navigate to={home} replace />;

  const today = new Date().toISOString().slice(0, 10);
  const adminPath = adminRootPath(claims);
  const showAdmin = canSeeAdmin(claims) && !!adminPath;

  const q = useQuery({
    queryKey: ["day-rollup", today],
    queryFn: () => rollupForDay(today),
    enabled: !!claims,
  });

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-agrilens-green-dark">
            Today · {today}
          </h1>
          <p className="text-sm text-gray-600 mb-6">
            {claims?.email ? `Signed in as ${claims.email}` : "Signed in"}
          </p>
        </div>
        {showAdmin && (
          <Link
            to={adminPath}
            className="bg-agrilens-green text-white text-sm font-medium px-4 py-2 rounded hover:bg-agrilens-green-dark"
          >
            Manage your area →
          </Link>
        )}
      </div>

      {q.isLoading && <div className="text-gray-600">Loading rollups…</div>}
      {q.error && (
        <div className="bg-red-50 text-red-700 p-3 rounded">
          Failed to load today's rollup: {q.error.message}
        </div>
      )}

      {q.data && q.data.scopes.length === 0 && (
        <div className="bg-yellow-50 text-yellow-800 p-3 rounded">
          {showAdmin ? (
            <>
              No activity yet for today. Head to{" "}
              <Link to={adminPath} className="font-semibold underline">
                your management area
              </Link>{" "}
              to set up districts, godowns, grain parameters, and invite
              your team — reports will show up here as they grade samples.
            </>
          ) : (
            <>
              No scopes visible for your account yet. Ask whoever invited
              you to grant a scope.
            </>
          )}
        </div>
      )}

      {q.data && q.data.scopes.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {q.data.scopes.map((s) => (
            <Link
              key={`${s.scope_type}-${s.scope_id}`}
              to={`/${s.scope_type}/${s.scope_id}`}
              className="block bg-white rounded shadow hover:shadow-md p-4 transition"
            >
              <div className="text-xs uppercase tracking-wider text-gray-500">
                {s.scope_type}
              </div>
              <div className="text-lg font-semibold mb-2">{s.scope_name}</div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <Stat label="RSTs" value={s.bucket.rst_count} />
                <Stat
                  label="Net (t)"
                  value={format.tonnes(s.bucket.net_weight_kg_total)}
                />
                <Stat label="Samples" value={s.bucket.sample_count} />
                <Stat
                  label="Pass"
                  value={`${s.bucket.pass_count}/${s.bucket.sample_count || 0}`}
                />
              </div>
            </Link>
          ))}
        </div>
      )}

      {q.data && (
        <div className="text-xs text-gray-400 mt-4">
          computed in {q.data.computed_in_ms} ms
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-gray-50 rounded px-2 py-1">
      <div className="text-gray-500 text-xs">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  );
}
